const MIN_PRESSURE = 0.025;
const RATE_OF_PRESSURE_CHANGE = 0.275;
const easeOutQuad = (t) => t * (2 - t);
const easeOutCubic = (t) => --t * t * t + 1;
const { min } = Math;
let pointCapacity = 256;
let pointX = new Float64Array(pointCapacity);
let pointY = new Float64Array(pointCapacity);
let inputX = new Float64Array(pointCapacity);
let inputY = new Float64Array(pointCapacity);
let inputZ = new Float64Array(pointCapacity);
let pressures = new Float64Array(pointCapacity);
let distances = new Float64Array(pointCapacity);
let runningLengths = new Float64Array(pointCapacity);
let radii = new Float64Array(pointCapacity);
let pointCount = 0;
function ensurePointCapacity(n) {
  if (n <= pointCapacity) return;
  while (pointCapacity < n) pointCapacity *= 2;
  pointX = new Float64Array(pointCapacity);
  pointY = new Float64Array(pointCapacity);
  inputX = new Float64Array(pointCapacity);
  inputY = new Float64Array(pointCapacity);
  inputZ = new Float64Array(pointCapacity);
  pressures = new Float64Array(pointCapacity);
  distances = new Float64Array(pointCapacity);
  runningLengths = new Float64Array(pointCapacity);
  radii = new Float64Array(pointCapacity);
}
let stageCapacity = 256;
let stageX = new Float64Array(stageCapacity);
let stageY = new Float64Array(stageCapacity);
let stageZ = new Float64Array(stageCapacity);
function ensureStageCapacity(n) {
  if (n <= stageCapacity) return;
  while (stageCapacity < n) stageCapacity *= 2;
  stageX = new Float64Array(stageCapacity);
  stageY = new Float64Array(stageCapacity);
  stageZ = new Float64Array(stageCapacity);
}
function zOf(p, clampZ) {
  const z = p.z === void 0 ? 1 : p.z;
  return clampZ && z < MIN_PRESSURE ? MIN_PRESSURE : z;
}
function ingest(rawInputPoints, options = {}) {
  const { streamline = 0.5, size = 16, simulatePressure = false } = options;
  pointCount = 0;
  const rawLen = rawInputPoints.length;
  if (rawLen === 0) return;
  const t = 0.15 + (1 - streamline) * 0.85;
  ensureStageCapacity(rawLen + 8);
  ensurePointCapacity(rawLen + 8);
  const stX = stageX;
  const stY = stageY;
  const stZ = stageZ;
  const minDist2 = (size / 3) ** 2;
  const clampZ = !simulatePressure;
  const first = rawInputPoints[0];
  let firstZ = zOf(first, clampZ);
  let startIdx = 1;
  while (startIdx < rawLen) {
    const pt = rawInputPoints[startIdx];
    const dx = pt.x - first.x;
    const dy = pt.y - first.y;
    if (dx * dx + dy * dy > minDist2) break;
    firstZ = Math.max(firstZ, zOf(pt, clampZ));
    startIdx++;
  }
  stX[0] = first.x;
  stY[0] = first.y;
  stZ[0] = firstZ;
  let m = 1;
  for (let i = startIdx; i < rawLen; i++) {
    const pt = rawInputPoints[i];
    stX[m] = pt.x;
    stY[m] = pt.y;
    stZ[m] = zOf(pt, clampZ);
    m++;
  }
  let pointsRemovedFromNearEnd = 0;
  if (m > 1) {
    const lastX = stX[m - 1];
    const lastY = stY[m - 1];
    let j = m - 2;
    while (j >= 0) {
      const dx = stX[j] - lastX;
      const dy = stY[j] - lastY;
      if (dx * dx + dy * dy > minDist2) break;
      j--;
      pointsRemovedFromNearEnd++;
    }
    if (j < m - 2) {
      stX[j + 1] = lastX;
      stY[j + 1] = lastY;
      stZ[j + 1] = stZ[m - 1];
      m = j + 2;
    }
  }
  const isComplete = options.last || !options.simulatePressure || m > 1 && (stX[m - 1] - stX[m - 2]) * (stX[m - 1] - stX[m - 2]) + (stY[m - 1] - stY[m - 2]) * (stY[m - 1] - stY[m - 2]) < size ** 2 || pointsRemovedFromNearEnd > 0;
  if (m === 2 && options.simulatePressure) {
    const x0 = stX[0];
    const y0 = stY[0];
    const z0 = stZ[0];
    const x1 = stX[1];
    const y1 = stY[1];
    const z1 = stZ[1];
    for (let i = 1; i < 5; i++) {
      const u2 = i / 4;
      stX[i] = x0 + (x1 - x0) * u2;
      stY[i] = y0 + (y1 - y0) * u2;
      stZ[i] = (z0 + (z1 - z0)) * i / 4;
    }
    m = 5;
  }
  const ptX = pointX;
  const ptY = pointY;
  const inX = inputX;
  const inY = inputY;
  const inZ = inputZ;
  const press = pressures;
  const dists = distances;
  const runs = runningLengths;
  const rads = radii;
  ptX[0] = stX[0];
  ptY[0] = stY[0];
  inX[0] = stX[0];
  inY[0] = stY[0];
  inZ[0] = stZ[0];
  press[0] = simulatePressure ? 0.5 : stZ[0];
  dists[0] = 0;
  runs[0] = 0;
  rads[0] = 1;
  let count = 1;
  if (isComplete && streamline > 0) {
    stX[m] = stX[m - 1];
    stY[m] = stY[m - 1];
    stZ[m] = stZ[m - 1];
    m++;
  }
  let totalLength = 0;
  let prevX = stX[0];
  let prevY = stY[0];
  const u = 1 - t;
  const isLast = options.last;
  for (let i = 1; i < m; i++) {
    let x, y;
    if (!t || isLast && i === m - 1) {
      x = stX[i];
      y = stY[i];
    } else {
      x = stX[i] + (prevX - stX[i]) * u;
      y = stY[i] + (prevY - stY[i]) * u;
    }
    if (Math.abs(prevX - x) < 1e-4 && Math.abs(prevY - y) < 1e-4) continue;
    const distance = ((y - prevY) ** 2 + (x - prevX) ** 2) ** 0.5;
    totalLength += distance;
    if (i < 4 && totalLength < size) continue;
    ptX[count] = x;
    ptY[count] = y;
    inX[count] = stX[i];
    inY[count] = stY[i];
    inZ[count] = stZ[i];
    press[count] = simulatePressure ? 0.5 : stZ[i];
    dists[count] = distance;
    runs[count] = totalLength;
    rads[count] = 1;
    count++;
    prevX = x;
    prevY = y;
  }
  if (totalLength < 1) {
    let max = 0.5;
    for (let i = 0; i < count; i++) max = Math.max(max, press[i]);
    for (let i = 0; i < count; i++) press[i] = max;
  }
  pointCount = count;
}
function resolveTaper(taper, size, totalLength) {
  if (!taper) return 0;
  return taper === true ? Math.max(size, totalLength) : taper;
}
function computeRadii(options) {
  const {
    size = 16,
    thinning = 0.5,
    simulatePressure = true,
    easing = (t) => t,
    start = {},
    end = {}
  } = options;
  const { easing: taperStartEase = easeOutQuad } = start;
  const { easing: taperEndEase = easeOutCubic } = end;
  const n = pointCount;
  const press = pressures;
  const dists = distances;
  const runs = runningLengths;
  const rads = radii;
  const totalLength = runs[n - 1];
  if (!simulatePressure && totalLength < size) {
    let max = 0.5;
    for (let i = 0; i < n; i++) max = Math.max(max, press[i]);
    for (let i = 0; i < n; i++) {
      press[i] = max;
      rads[i] = size * easing(0.5 - thinning * (0.5 - max));
    }
    return;
  }
  let prevPressure = press[0];
  for (let i = 0; i < n; i++) {
    if (runs[i] > size * 5) break;
    const sp = min(1, dists[i] / size);
    let p;
    if (simulatePressure) {
      const rp = min(1, 1 - sp);
      p = min(1, prevPressure + (rp - prevPressure) * (sp * RATE_OF_PRESSURE_CHANGE));
    } else {
      p = min(1, prevPressure + (press[i] - prevPressure) * 0.5);
    }
    prevPressure = prevPressure + (p - prevPressure) * 0.5;
  }
  const taperStart = resolveTaper(start.taper, size, totalLength);
  const taperEnd = resolveTaper(end.taper, size, totalLength);
  const hasTaper = taperStart || taperEnd;
  for (let i = 0; i < n; i++) {
    let radius;
    if (thinning) {
      let pressure = press[i];
      const sp = min(1, dists[i] / size);
      if (simulatePressure) {
        const rp = min(1, 1 - sp);
        pressure = min(1, prevPressure + (rp - prevPressure) * (sp * RATE_OF_PRESSURE_CHANGE));
      } else {
        pressure = min(1, prevPressure + (pressure - prevPressure) * (sp * RATE_OF_PRESSURE_CHANGE));
      }
      radius = size * easing(0.5 - thinning * (0.5 - pressure));
      prevPressure = pressure;
    } else {
      radius = size / 2;
    }
    if (hasTaper) {
      const runningLength = runs[i];
      const ts = runningLength < taperStart ? taperStartEase(runningLength / taperStart) : 1;
      const te = totalLength - runningLength < taperEnd ? taperEndEase((totalLength - runningLength) / taperEnd) : 1;
      radius = Math.max(0.01, radius * Math.min(ts, te));
    }
    rads[i] = radius;
  }
}
let srcCapacity = 256;
let srcX = new Float64Array(srcCapacity);
let srcY = new Float64Array(srcCapacity);
let srcZ = new Float64Array(srcCapacity);
let srcInputX = new Float64Array(srcCapacity);
let srcInputY = new Float64Array(srcCapacity);
let srcRadius = new Float64Array(srcCapacity);
let srcRunningLength = new Float64Array(srcCapacity);
let srcIsCap = new Uint8Array(srcCapacity);
let srcCount = 0;
function ensureSrcCapacity(n) {
  if (n <= srcCapacity) return;
  while (srcCapacity < n) srcCapacity *= 2;
  srcX = new Float64Array(srcCapacity);
  srcY = new Float64Array(srcCapacity);
  srcZ = new Float64Array(srcCapacity);
  srcInputX = new Float64Array(srcCapacity);
  srcInputY = new Float64Array(srcCapacity);
  srcRadius = new Float64Array(srcCapacity);
  srcRunningLength = new Float64Array(srcCapacity);
  srcIsCap = new Uint8Array(srcCapacity);
}
function loadSrcFromStrokePoints(strokePoints) {
  const n = strokePoints.length;
  ensureSrcCapacity(n);
  const sx = srcX;
  const sy = srcY;
  const sz = srcZ;
  const six = srcInputX;
  const siy = srcInputY;
  const sr = srcRadius;
  const srl = srcRunningLength;
  const scap = srcIsCap;
  const first = strokePoints[0];
  const last = strokePoints[n - 1];
  for (let i = 0; i < n; i++) {
    const sp = strokePoints[i];
    const point = sp.point;
    const input = sp.input;
    sx[i] = point.x;
    sy[i] = point.y;
    sz[i] = point.z;
    six[i] = input.x;
    siy[i] = input.y;
    sr[i] = sp.radius;
    srl[i] = sp.runningLength;
    scap[i] = sp === first || sp === last ? 1 : 0;
  }
  srcCount = n;
}
function loadSrcFromPipeline() {
  const n = pointCount;
  ensureSrcCapacity(n);
  const sx = srcX;
  const sy = srcY;
  const sz = srcZ;
  const six = srcInputX;
  const siy = srcInputY;
  const sr = srcRadius;
  const srl = srcRunningLength;
  const scap = srcIsCap;
  const ptX = pointX;
  const ptY = pointY;
  const inX = inputX;
  const inY = inputY;
  const inZ = inputZ;
  const runs = runningLengths;
  const rads = radii;
  for (let i = 0; i < n; i++) {
    sx[i] = ptX[i];
    sy[i] = ptY[i];
    sz[i] = inZ[i];
    six[i] = inX[i];
    siy[i] = inY[i];
    sr[i] = rads[i];
    srl[i] = runs[i];
    scap[i] = i === 0 || i === n - 1 ? 1 : 0;
  }
  srcCount = n;
}
function loadSrcPartition(a, aElbow, innerStart, innerEnd, b, bElbow, dupQuirk) {
  ensureSrcCapacity(innerEnd - innerStart + 3);
  const sx = srcX;
  const sy = srcY;
  const sz = srcZ;
  const six = srcInputX;
  const siy = srcInputY;
  const sr = srcRadius;
  const srl = srcRunningLength;
  const scap = srcIsCap;
  const ptX = pointX;
  const ptY = pointY;
  const inX = inputX;
  const inY = inputY;
  const inZ = inputZ;
  const runs = runningLengths;
  const rads = radii;
  sx[0] = aElbow ? inX[a] : ptX[a];
  sy[0] = aElbow ? inY[a] : ptY[a];
  sz[0] = inZ[a];
  six[0] = inX[a];
  siy[0] = inY[a];
  sr[0] = rads[a];
  srl[0] = runs[a];
  scap[0] = 1;
  let w = 1;
  for (let i = innerStart; i <= innerEnd; i++) {
    sx[w] = ptX[i];
    sy[w] = ptY[i];
    sz[w] = inZ[i];
    six[w] = inX[i];
    siy[w] = inY[i];
    sr[w] = rads[i];
    srl[w] = runs[i];
    scap[w] = 0;
    w++;
  }
  if (dupQuirk) scap[w - 1] = 1;
  sx[w] = bElbow ? inX[b] : ptX[b];
  sy[w] = bElbow ? inY[b] : ptY[b];
  sz[w] = inZ[b];
  six[w] = inX[b];
  siy[w] = inY[b];
  sr[w] = rads[b];
  srl[w] = runs[b];
  scap[w] = 1;
  srcCount = w + 1;
}
export {
  computeRadii,
  distances,
  ingest,
  inputX,
  inputY,
  inputZ,
  loadSrcFromPipeline,
  loadSrcFromStrokePoints,
  loadSrcPartition,
  pointCount,
  pointX,
  pointY,
  pressures,
  radii,
  resolveTaper,
  runningLengths,
  srcCount,
  srcInputX,
  srcInputY,
  srcIsCap,
  srcRadius,
  srcRunningLength,
  srcX,
  srcY,
  srcZ
};
//# sourceMappingURL=core.mjs.map
